// FT-FOCUS — the items a user pointed at, by clicking the editor preview or
// by picking from the composer's @-menu.
//
// Shared between PortalPreview (which produces them from the rendered DOM),
// PortalView (which holds the selection) and ChatSidebar (which shows them as
// chips, offers the @-menu, and sends them with the message).

export interface FocusBlock {
  id: string;
  kind: string;
  label: string;
  /**
   * For @-menu picks: the exact text left in the composer for this item
   * ("@Twitter"). It stays in the sentence and is echoed to the model beside
   * the block, which is what lets one message give two picks different
   * instructions ("remove the @Facebook item, move @Twitter to the top").
   * Undefined for preview clicks, which have no place in the prose.
   */
  mention?: string;
}

// Kinds that render no text of their own, so a label has to be supplied.
const FALLBACK_LABELS: Record<string, string> = {
  avatar: "Profile picture",
  youtube: "YouTube video",
  vimeo: "Vimeo video",
  calendly: "Calendly booking",
  iframe_safe: "Embedded page",
  stripe_buy_button: "Stripe buy button",
};

const KIND_ICONS: Record<string, string> = {
  title: "bi-type-h1",
  description: "bi-card-text",
  avatar: "bi-person-circle",
  link: "bi-link-45deg",
  heading: "bi-type-h2",
  text: "bi-text-paragraph",
  youtube: "bi-youtube",
  vimeo: "bi-vimeo",
  calendly: "bi-calendar-event",
  iframe_safe: "bi-window",
  stripe_buy_button: "bi-credit-card",
};

export function focusIcon(kind: string): string {
  return KIND_ICONS[kind] ?? "bi-bullseye";
}

const MAX_LABEL = 80;

// Builds the display label from what the element actually renders, so a chip
// reads as the thing on screen ("About my work") rather than an opaque id.
// Captured at click time and stored with the message: chat history should keep
// showing what the user pointed at even after that text changes.
export function focusLabel(el: Element, kind: string): string {
  const source = el.querySelector(".link-label") ?? el;
  const text = (source.textContent ?? "").trim().replace(/\s+/g, " ");
  if (!text) return FALLBACK_LABELS[kind] ?? kind;
  return text.length > MAX_LABEL ? `${text.slice(0, MAX_LABEL - 1)}…` : text;
}

// Every whitespace-separated token in the query must appear somewhere in the
// item's label or kind. AND-of-tokens rather than a single substring test so a
// query can skip words: "@prof life" still finds "My professional life", which
// is the whole point of typing a few characters instead of the exact text.
// Kind is searchable too, so "@link" lists every link.
export function matchesQuery(item: FocusBlock, query: string): boolean {
  const haystack = `${item.label} ${item.kind}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token));
}

// Matches a trailing "@query" the user is typing. Spaces are allowed inside
// the query because labels have them ("@my professional"), which means the
// pattern alone can't tell a mention from prose ("email me @ the address").
// The caller closes the menu whenever a match yields no items, so a false
// positive costs one render and then disappears on its own.
const MENTION_RE = /(?:^|\s)@([^@\n]{0,40})$/;

export interface MentionQuery {
  /** Text between the "@" and the caret. */
  query: string;
  /** Index of the "@" in the source text, for replacing on select. */
  at: number;
}

export function mentionQueryAt(text: string): MentionQuery | null {
  const m = MENTION_RE.exec(text);
  if (!m) return null;
  // The pattern is anchored to the end, so the "@" sits exactly one character
  // before the captured query.
  return { query: m[1], at: text.length - m[1].length - 1 };
}

// Inline mentions are shorter than chip labels: this text sits inside a
// sentence the user is still writing, and an 80-character paragraph snippet
// dropped mid-clause is unreadable. The model never has to guess whether it
// was shortened — the appendix quotes this exact string next to the block.
const MAX_MENTION = 32;

export function mentionText(label: string): string {
  const flat = label.replace(/\s+/g, " ").trim();
  return `@${flat.length > MAX_MENTION ? `${flat.slice(0, MAX_MENTION - 1)}…` : flat}`;
}

// Two items can read the same for the first few words ("Filler paragraph
// number 1" / "...2" once truncated). The mention is the ONLY thing binding a
// word in the sentence to a block id, so letting two picks share one would
// reintroduce exactly the ambiguity this feature removes.
export function uniqueMention(label: string, taken: string[]): string {
  const base = mentionText(label);
  if (!taken.includes(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base} #${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
}
